import type { PixelConfig } from '@/config';
import type { BrowserMetadataService } from '@/services/browser-metadata';
import type { StorageService } from '@/services/storage';

import type { FingerprintService } from './types';

const DEFAULT_FINGERPRINT_KEY = 'fingerprint';

function hashFingerprintInput(input: string): string {
  let hash = 0;

  for (let index = 0; index < input.length; index++) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0;
  }

  const positiveHash = Math.abs(hash).toString(16).padStart(8, '0');
  let secondaryHash = 0;

  for (let index = input.length - 1; index >= 0; index--) {
    secondaryHash =
      (secondaryHash << 5) - secondaryHash + input.charCodeAt(index);
    secondaryHash |= 0;
  }

  return `fp_${positiveHash}${Math.abs(secondaryHash).toString(16).padStart(8, '0')}`;
}

function computeLightFingerprint(
  metadataService: BrowserMetadataService
): string {
  const metadata = metadataService.getMetadata();

  const components = [
    metadata.userAgent ?? '',
    metadata.language ?? '',
    metadata.platform ?? '',
    String(metadata.screenWidth ?? ''),
    String(metadata.screenHeight ?? ''),
    String(metadata.colorDepth ?? ''),
    metadata.timezone ?? '',
  ];

  return hashFingerprintInput(components.join('|'));
}

export class FingerprintServiceImpl implements FingerprintService {
  private readonly storage: StorageService;

  private readonly storageKey: string;

  private readonly metadataService: BrowserMetadataService;

  constructor(
    storage: StorageService,
    config: PixelConfig,
    metadataService: BrowserMetadataService
  ) {
    this.storage = storage;
    this.storageKey =
      config.storage.keys?.fingerprint ?? DEFAULT_FINGERPRINT_KEY;
    this.metadataService = metadataService;
  }

  getFingerprint(): string | null {
    return this.storage.get<string>(this.storageKey);
  }

  ensureFingerprint(): string {
    const existing = this.getFingerprint();
    if (existing) return existing;

    const fingerprint = computeLightFingerprint(this.metadataService);
    this.storage.set(this.storageKey, fingerprint);
    return fingerprint;
  }
}
